/*
 * WO-36 — shared pieces for the admin analytics screens.
 *
 * The rule these screens now follow: a number is either MEASURED from the
 * database, or the API returns null and the screen says "Not measured yet".
 * A failed request says so; it never shows zeros that look like real data.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { AlertCircle, Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** GET an admin endpoint: { data, loading, error, reload }. */
export function useAdminData(path) {
  const { getToken } = useAuth();
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await fetch(`${API_URL}${path}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setState({ data: null, loading: false, error: body.detail || `The server answered ${res.status}.` });
      } else {
        setState({ data: body, loading: false, error: null });
      }
    } catch (e) {
      setState({ data: null, loading: false, error: 'The server could not be reached.' });
    }
  }, [path, getToken]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

export const isMeasured = (v) => v !== null && v !== undefined;

export const money = (v) => {
  if (!isMeasured(v)) return null;
  const n = Number(v);
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 10000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: n % 1 ? 2 : 0 })}`;
};

export const pct = (v) => (isMeasured(v) ? `${v}%` : null);

export const count = (v) => (isMeasured(v) ? Number(v).toLocaleString('en-US') : null);

/** The grey "Not measured yet" line that replaces an invented number. */
export function NotMeasured({ why }) {
  return (
    <span className="text-sm font-normal text-muted-foreground" title={why || ''} data-testid="not-measured">
      Not measured yet
    </span>
  );
}

/** A value, or "Not measured yet" when the API sent null. */
export function Value({ children, why, className = 'text-2xl font-bold' }) {
  if (children === null || children === undefined) return <p className="mt-1"><NotMeasured why={why} /></p>;
  return <p className={className}>{children}</p>;
}

/** A simple metric card: label, value (or Not measured yet), one-line note. */
export function Metric({ label, value, note, why, icon: Icon, testid }) {
  return (
    <Card className="border-0 bg-card/50" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">{label}</p>
            <Value why={why}>{value}</Value>
            {note && <p className="text-xs text-muted-foreground mt-1">{note}</p>}
          </div>
          {Icon && <Icon className="w-5 h-5 text-primary shrink-0" />}
        </div>
      </CardContent>
    </Card>
  );
}

export function LoadingBlock({ testid }) {
  return (
    <div className="space-y-6" data-testid={testid}>
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-28" />)}
      </div>
    </div>
  );
}

export function ErrorBox({ message, onRetry }) {
  return (
    <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3" data-testid="admin-load-error">
      <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
      <div className="text-sm">
        <p className="font-medium">This page could not load its figures.</p>
        <p className="text-muted-foreground">{message}</p>
        {onRetry && (
          <button type="button" onClick={onRetry} className="mt-2 underline text-primary">Try again</button>
        )}
      </div>
    </div>
  );
}

/** Says who is left out of the figures, so nobody wonders why totals differ. */
export function ExcludedNote({ excluded }) {
  if (!excluded) return null;
  return (
    <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="excluded-note">
      <Info className="w-3.5 h-3.5" />
      Customers only: {excluded.internal_users} admin/internal people
      {excluded.internal_companies ? ` and ${excluded.internal_companies} internal companies` : ''} are left out.
      Mark test and staff accounts as internal in Users Management.
    </p>
  );
}

export function SectionNote({ children }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}
